import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Progress,
  Tabs,
  Table,
  Tag,
  Tooltip,
  Button,
  Typography,
  Space,
  Input,
  Select,
  Avatar,
  Empty,
  Statistic,
} from "antd";
import {
  getEmailSequenceAnalytics,
  getSequenceRecipients
} from "@api/email/emailSequence";
import { useParams, useHistory } from "react-router-dom";
import {
  EyeOutlined,
  LinkOutlined,
  DownloadOutlined,
  ReloadOutlined,
  GlobalOutlined,
  MobileOutlined,
  MailOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DesktopOutlined,
  ArrowLeftOutlined,
  LineChartOutlined,
  FilterOutlined,
  UserOutlined,
  PlayCircleOutlined,
  StopOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { format } from "date-fns";
import { debounce } from "lodash";
import "@assets/css/sentEmailsPage.css";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

// Domain colors for recipient avatars
const domainColors = {
  gmail: "#ea4335",
  yahoo: "#720e9e", 
  outlook: "#0078d4",
  hotmail: "#0078d4",
  icloud: "#007aff",
  other: "#52c41a"
};

const EmailSequenceAnalytics = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    searchText: "",
  });
  const [activeTab, setActiveTab] = useState("1");

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await getEmailSequenceAnalytics(id);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error("Error fetching sequence analytics:", error);
      message.error("Failed to fetch sequence analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const { status, searchText } = filters;
      const { current, pageSize } = pagination;

      const response = await getSequenceRecipients(id, {
        page: current,
        perPage: pageSize,
        status,
        searchText: searchText?.trim(),
        sortBy: "startedAt",
        sortOrder: "desc",
      });

      if (response.data.success) {
        const { recipients: recipientData, pagination: paginationData } = response.data.data;
        setRecipients(recipientData);
        setPagination({
          current: paginationData.currentPage,
          pageSize: paginationData.itemsPerPage,
          total: paginationData.totalItems,
        });
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
      message.error("Failed to load recipients");
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchRecipients();
  }, [id]);

  useEffect(() => {
    fetchRecipients();
  }, [pagination.current, pagination.pageSize, filters]);

  // Debounced search handler
  const debouncedSearch = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchAnalytics();
      await fetchRecipients();
      message.success("Analytics refreshed successfully");
    } catch (error) {
      message.error("Failed to refresh analytics");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: paginationInfo.total,
    });

    if (filters.status?.length) {
      handleFilterChange("status", filters.status[0]);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: undefined,
      searchText: "",
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Extract domain from email
  const extractDomain = (email) => {
    if (!email) return "unknown";
    const parts = email.split("@");
    return parts.length > 1 ? parts[1].split(".")[0].toLowerCase() : "unknown";
  };

  // Get status tag class
  const getStatusClass = (status) => {
    const statusMap = {
      completed: "se-tag-success",
      in_progress: "se-tag-processing", 
      pending: "se-tag-warning",
      stopped: "se-tag-error"
    };
    return statusMap[status] || "se-tag-default";
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const iconMap = {
      completed: <CheckCircleOutlined />,
      in_progress: <PlayCircleOutlined />,
      pending: <PauseCircleOutlined />,
      stopped: <StopOutlined />
    };
    return iconMap[status] || <InfoCircleOutlined />;
  };

  // Get device icon
  const getDeviceIcon = (device) => {
    if (!device) return <InfoCircleOutlined />;
    return device.toLowerCase().includes("mobile") ? (
      <MobileOutlined />
    ) : (
      <DesktopOutlined />
    );
  };

  if (loading && !recipients.length) {
    return (
      <div className="se-container">
        <div className="se-loading">
          <Spin size="large" />
          <div style={{ marginTop: "20px" }}>Loading sequence analytics...</div>
        </div>
      </div>
    );
  }

  // Recipients table columns
  const recipientColumns = [
    {
      title: "Recipient",
      key: "recipient",
      render: (_, record) => {
        const domain = extractDomain(record.email);

        return (
          <Tooltip
            title={record.user ? record.user.email : record.email}
            className="se-tooltip"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Avatar
                style={{
                  backgroundColor: domainColors[domain] || domainColors.other,
                  marginRight: "8px",
                }}
                size="small"
              >
                {(
                  record.user?.name?.charAt(0) ||
                  record.email?.charAt(0) ||
                  "?"
                ).toUpperCase()}
              </Avatar>
              <div>
                <div style={{ fontWeight: "500" }}>
                  {record.user ? record.user.email : record.email}
                </div>
                <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  {record.user?.name && <span>Name: {record.user.name}</span>}
                  {record.triggerType && (
                    <span> • Trigger: {record.triggerType}</span>
                  )}
                </div>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "140px",
      render: (status) => {
        const statusColors = {
          completed: "success",
          in_progress: "processing",
          pending: "warning",
          stopped: "error"
        };
        return (
          <Tag 
            className={`se-tag ${getStatusClass(status)}`}
            color={statusColors[status] || "default"}
          >
            {getStatusIcon(status)}{" "}
            {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
          </Tag>
        );
      },
      filters: [
        { text: "Completed", value: "completed" },
        { text: "In Progress", value: "in_progress" },
        { text: "Pending", value: "pending" },
        { text: "Stopped", value: "stopped" },
      ],
    },
    {
      title: "Progress",
      key: "progress",
      width: "160px",
      render: (_, record) => {
        const progressPercent = record.status === 'completed' ? 100 : 
          ((record.currentEmailIndex + 1) / (analyticsData?.emails?.length || 1)) * 100;
        
        return (
          <div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
              <Text strong>Step {record.currentEmailIndex + 1}</Text>
              <Tag size="small" color="blue" style={{ marginLeft: "8px" }}>
                {Math.round(progressPercent)}%
              </Tag>
            </div>
            <Progress 
              percent={progressPercent} 
              size="small" 
              showInfo={false}
              strokeColor={record.status === 'completed' ? '#52c41a' : '#1890ff'}
            />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.totalEmailsSent} emails sent
            </Text>
          </div>
        );
      },
    },
    {
      title: "Engagement",
      key: "engagement", 
      width: "200px",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Open Details</div>
                <p>
                  Total Opens: {record.engagement?.openCount || 0}
                </p>
                <p>
                  First Open:{" "}
                  {record.engagement?.firstOpenedAt
                    ? format(new Date(record.engagement.firstOpenedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Open:{" "}
                  {record.engagement?.lastOpenedAt
                    ? format(new Date(record.engagement.lastOpenedAt), "PPpp")
                    : "Never"}
                </p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.opened ? "se-tag-open" : ""
              }`}
            >
              <EyeOutlined /> {record.engagement?.openCount || 0}
            </Tag>
          </Tooltip>
          <Tooltip
            title={
              <div className="se-tooltip-content">
                <div className="se-tooltip-title">Click Details</div>
                <p>
                  Total Clicks: {record.engagement?.clickCount || 0}
                </p>
                <p>
                  First Click:{" "}
                  {record.engagement?.firstClickedAt
                    ? format(new Date(record.engagement.firstClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Last Click:{" "}
                  {record.engagement?.lastClickedAt
                    ? format(new Date(record.engagement.lastClickedAt), "PPpp")
                    : "Never"}
                </p>
                <p>
                  Links Clicked: {record.engagement?.clickedLinks?.length || 0}
                </p>
              </div>
            }
            className="se-tooltip"
          >
            <Tag
              className={`se-tag ${
                record.engagement?.clicked ? "se-tag-click" : ""
              }`}
            >
              <LinkOutlined /> {record.engagement?.clickCount || 0}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Domain",
      key: "domain",
      width: "120px",
      render: (_, record) => {
        const domain = extractDomain(record.email);
        return (
          <Tag
            style={{
              backgroundColor: `${
                domainColors[domain] || domainColors.other
              }22`,
              color: domainColors[domain] || domainColors.other,
              border: `1px solid ${domainColors[domain] || domainColors.other}`,
            }}
            className="se-tag"
          >
            <GlobalOutlined /> {domain}
          </Tag>
        );
      },
    },
    {
      title: "Started At",
      dataIndex: "startedAt",
      key: "startedAt",
      width: "180px",
      render: (date) => date ? (
        <span>
          <CalendarOutlined style={{ marginRight: "5px" }} />
          {format(new Date(date), "PPp")}
        </span>
      ) : (
        <Text type="secondary">Not started</Text>
      ),
      sorter: (a, b) => new Date(a.startedAt || 0) - new Date(b.startedAt || 0),
    },
  ];


  const goBack = () => {
    const basePath = history.location.pathname.includes("/super-admin")
      ? "/super-admin"
      : "/marketing-admin";
    history.push(`${basePath}/email/email-sequences`);
  };

  return (
    <div className="se-container">
      {/* Header */}
      <div className="se-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              style={{ marginRight: "10px" }}
            >
              Back to Sequences
            </Button>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={handleRefresh}
                className="se-btn"
                style={{
                  marginRight: "5px",
                  backgroundColor: "#fff",
                  color: "rgba(0, 0, 0, 0.65)",
                  border: "1px solid #d9d9d9",
                  fontWeight: "500",
                }}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => message.info("Export functionality coming soon")}
                className="se-btn"
                style={{
                  marginRight: "5px",
                  backgroundColor: "#1890ff",
                  color: "white",
                  border: "none",
                  fontWeight: "500",
                  boxShadow: "0 2px 0 rgba(0, 0, 0, 0.045)",
                }}
              >
                Export Data
              </Button>
              <Button
                icon={<UserOutlined />}
                onClick={() => setActiveTab("2")}
                className="se-btn"
                style={{
                  backgroundColor: "#52c41a",
                  color: "white",
                  border: "none",
                  fontWeight: "500",
                  boxShadow: "0 2px 0 rgba(0, 0, 0, 0.045)",
                }}
              >
                View Recipients
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "0 24px" }}>
        {/* Title */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={2} className="se-title">
            <MailOutlined style={{ marginRight: "10px", color: "#1890ff" }} />
            {analyticsData?.sequenceName || "Sequence Analytics"}
          </Title>
          <Text type="secondary" className="se-subtitle">
            Email sequence performance metrics and recipient journey tracking
          </Text>
        </div>

        {/* Stats Section */}
        <Row gutter={16} style={{ marginBottom: "24px" }}>
          <Col xs={24} md={24} style={{ marginBottom: "12px" }}>
            <Card
              bordered={false}
              className="se-card"
              style={{ background: "#f9f9f9" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Text strong>Sequence ID:</Text>
                  <Text
                    copyable={{ text: id }}
                    style={{ marginLeft: "8px", fontSize: "16px" }}
                  >
                    {id}
                  </Text>
                </div>
                <Tag
                  color="blue"
                  style={{ fontWeight: "500", textTransform: "uppercase" }}
                >
                  SEQUENCE IDENTIFIER
                </Tag>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card bordered={false} className="se-card se-stats-card">
              <div className="se-stats-value">
                {analyticsData?.summary?.totalRecipients || 0}
              </div>
              <div className="se-stats-title">Total Recipients</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-success"
            >
              <div className="se-stats-value" style={{ color: "#52c41a" }}>
                {analyticsData?.summary?.status?.completed || 0}
              </div>
              <div className="se-stats-title">Completed Journeys</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-open"
            >
              <div className="se-stats-value" style={{ color: "#1890ff" }}>
                {analyticsData?.engagement?.rates?.openRate?.toFixed(1) || 0}%
              </div>
              <div className="se-stats-title">Open Rate</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              bordered={false}
              className="se-card se-stats-card se-stats-click"
            >
              <div className="se-stats-value" style={{ color: "#722ed1" }}>
                {analyticsData?.engagement?.rates?.clickRate?.toFixed(1) || 0}%
              </div>
              <div className="se-stats-title">Click Rate</div>
            </Card>
          </Col>
        </Row>

        {/* Progress Card */}
        <Card
          bordered={false}
          className="se-card se-progress-card"
          style={{ marginBottom: "24px" }}
        >
          <Row gutter={24} align="middle">
            <Col xs={24} md={8}>
              <div className="se-progress-container">
                <Progress
                  type="circle"
                  percent={(
                    ((analyticsData?.summary?.status?.completed || 0) /
                      (analyticsData?.summary?.totalRecipients || 1)) *
                    100
                  ).toFixed(1)}
                  status="active"
                  width={150}
                  strokeWidth={10}
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }}
                />
                <div className="se-progress-title">Sequence Progress</div>
                <Tag className="se-tag" color="processing">
                  {analyticsData?.summary?.status?.inProgress || 0} Active Users
                </Tag>
              </div>
            </Col>
            <Col xs={24} md={16}>
              <div className="se-metrics-grid">
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.summary?.totalRecipients?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Total Recipients</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.summary?.status?.completed?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Completed</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.summary?.status?.inProgress?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">In Progress</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.engagement?.total?.opens?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Total Opens</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.engagement?.total?.clicks?.toLocaleString() || 0}
                  </div>
                  <div className="se-metric-label">Total Clicks</div>
                </div>
                <div className="se-metric-item">
                  <div className="se-metric-value">
                    {analyticsData?.engagement?.rates?.clickToOpenRate?.toFixed(1) || 0}%
                  </div>
                  <div className="se-metric-label">Click-to-Open</div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Tabs Content */}
        <Card
          bordered={false}
          className="se-card se-tabs-card"
          style={{ marginBottom: "24px" }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            size="large"
            className="se-tabs"
            animated={{ inkBar: true, tabPane: true }}
            items={[
              {
                key: "1",
                label: (
                  <span>
                    <MailOutlined /> Overview
                  </span>
                ),
                children: (
                  <Row gutter={[16, 16]}>
                    {/* Email Performance Table */}
                    {analyticsData?.emails && analyticsData.emails.length > 0 && (
                      <Col span={24}>
                        <Card title="Email Performance by Step" className="se-card">
                          <Table
                            columns={[
                              {
                                title: "Step",
                                key: "step",
                                render: (_, record, index) => (
                                  <Space>
                                    <Tag color="blue">{index + 1}</Tag>
                                    <Text strong>{record.subject || "Untitled"}</Text>
                                  </Space>
                                ),
                              },
                              {
                                title: "Sent",
                                dataIndex: ["metrics", "sent"],
                                key: "sent",
                              },
                              {
                                title: "Opens",
                                key: "opens",
                                render: (_, record) => (
                                  <Space>
                                    <Text>{record.metrics?.opened || 0}</Text>
                                    <Tag color="green">{record.rates?.openRate?.toFixed(1) || 0}%</Tag>
                                  </Space>
                                ),
                              },
                              {
                                title: "Clicks",
                                key: "clicks",
                                render: (_, record) => (
                                  <Space>
                                    <Text>{record.metrics?.clicked || 0}</Text>
                                    <Tag color="blue">{record.rates?.clickRate?.toFixed(1) || 0}%</Tag>
                                  </Space>
                                ),
                              },
                            ]}
                            dataSource={analyticsData.emails}
                            rowKey={(record, index) => index}
                            pagination={false}
                            size="small"
                          />
                        </Card>
                      </Col>
                    )}
                    
                    {/* Additional Overview Cards */}
                    <Col span={12}>
                      <Card title="Trigger Breakdown" className="se-card">
                        {analyticsData?.triggers?.byType ? (
                          <div>
                            {Object.entries(analyticsData.triggers.byType).map(([trigger, count]) => (
                              <Row key={trigger} style={{ marginBottom: 8 }}>
                                <Col span={16}>
                                  <Text>{trigger.charAt(0).toUpperCase() + trigger.slice(1)}</Text>
                                </Col>
                                <Col span={8}>
                                  <Tag color="orange">{count} users</Tag>
                                </Col>
                              </Row>
                            ))}
                          </div>
                        ) : (
                          <Empty description="No trigger data available" />
                        )}
                      </Card>
                    </Col>
                    
                    <Col span={12}>
                      <Card title="Engagement Summary" className="se-card">
                        <div style={{ padding: "16px 0" }}>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Statistic
                                title="Average Open Rate"
                                value={analyticsData?.engagement?.rates?.openRate?.toFixed(1) || 0}
                                suffix="%"
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Col>
                            <Col span={12}>
                              <Statistic
                                title="Average Click Rate"
                                value={analyticsData?.engagement?.rates?.clickRate?.toFixed(1) || 0}
                                suffix="%"
                                valueStyle={{ color: '#1890ff' }}
                              />
                            </Col>
                          </Row>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                ),
              },

              {
                key: "2",
                label: (
                  <span>
                    <UserOutlined /> Recipients
                  </span>
                ),
                children: (
                  <div>
                    {/* Section Title */}
                    <h3 className="se-section-title">
                      <LineChartOutlined style={{ marginRight: "8px" }} />
                      Recipient Journey Tracking
                    </h3>
                    
                    {/* Filters */}
                    <Card
                      bordered={false}
                      className="se-card se-filters-card"
                      style={{ marginBottom: "16px" }}
                    >
                      <Row gutter={16} className="se-filter-row">
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                            Status
                          </div>
                          <Select
                            placeholder="Filter by status"
                            style={{ width: "100%" }}
                            value={filters.status}
                            onChange={(value) => handleFilterChange("status", value)}
                            allowClear
                          >
                            <Option value="completed">Completed</Option>
                            <Option value="in_progress">In Progress</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="stopped">Stopped</Option>
                          </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                            Search
                          </div>
                          <Search
                            placeholder="Search by email or name"
                            value={filters.searchText}
                            onChange={(e) => debouncedSearch(e.target.value)}
                            style={{ width: "100%" }}
                            allowClear
                          />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                            Actions
                          </div>
                          <Button icon={<FilterOutlined />} onClick={clearFilters}>
                            Clear Filters
                          </Button>
                        </Col>
                      </Row>
                    </Card>

                    {/* Recipients Table */}
                    <div className="se-table-container">
                      <Table
                        columns={recipientColumns}
                        dataSource={recipients}
                        loading={loading}
                        pagination={{
                          current: pagination.current,
                          pageSize: pagination.pageSize,
                          total: pagination.total,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total) => `Total ${total} recipients`,
                        }}
                        onChange={handleTableChange}
                        rowKey="_id"
                        scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
                        locale={{
                          emptyText: (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No recipients found"
                            />
                          ),
                        }}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
};

export default EmailSequenceAnalytics;